<template>
  <div class="page-block">
    <div class="page-block__heading page-block__heading--withbar">
      <h3 class="page-block__title">SMS認証</h3>
    </div>
    <div class="page-block__inner">
      <release-step-area
        v-if="!hideSteps"
        :index="4" />
      <div class="sms-authentication-area">
        <div class="sms-authentication-area-title">電話番号の登録</div>
        <p class="sms-authentication-area-paragraph">
          個人情報の確認のため、SMS認証を行います。<br />
          SMS認証に使用する携帯電話番号を入力してください。<br /><br />
          認証コード送信ボタンを押すと、入力した携帯電話番号に認証コードが送信されます。<br />
          <br />
          <span>※ 一度登録した電話番号は変更できません。</span>
        </p>
        <div class="form-group b-0">
          <div class="form-item">
            <input
              v-model="mobileNumber"
              class="form-control"
              type="text"
              placeholder="例）09011112222"
              maxlength="11"
              :readonly="isReadOnly()"
              @input="onInput()" />
            <p
              v-if="errorMessage"
              class="error-text">
              {{ errorMessage }}
            </p>
          </div>
        </div>
      </div>
      <div class="c-btn-wrap c-btn-wrap--two-btn">
        <a
          class="c-btn"
          :class="{ 'c-btn--off' : disableBtnClick }"
          :disabled="disableBtnClick"
          @click="submitMobileNumber">
          {{ isFirstTime ? '認証コード送信' : '認証コード再送信' }}
          <span class="c-btn__icon-arrow"></span>
        </a>
        <a
          class="c-btn c-btn--back c-btn--back-left"
          @click="onReturnClicked()">
          戻る
          <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import ReleaseStepArea from '../Release/StepArea'

export default {
  name: 'PhoneRegistration',
  components: {
    ReleaseStepArea,
  },
  props: {
    parentMessage: {
      type: String,
      default: '',
      required: false,
    },
    isFirstTime: {
      type: Boolean,
      required: true,
      default: true,
    },
    savedMobile: {
      type: String,
      required: true,
    },
    hideSteps: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mobileNumber: '',
      errorMessage: this.parentMessage,
      countryCode: '+81',
      isLoading: false,
      disableBtnClick: false,
    }
  },
  mounted() {
    if (this.savedMobile) {
      this.mobileNumber = this.savedMobile
    } else {
      // track registering of mobile number for first time
      window.sessionStorage.setItem('new_mobile_registration', 'true')
    }
  },
  methods: {
    // sanitize input to include only numbers
    sanitizeInput() {
      const validInputRegex = /^[0-9０-９]+$/
      if (validInputRegex.test(this.mobileNumber)) {
        this.mobileNumber = this.mobileNumber.replace(/[^0-9０-９]+/g, '')
      } else {
        this.mobileNumber = ''
      }
    },
    // convert full width mobile number to half width
    convertToHalfWidth(mobileNo) {
      const fullWidthNumbers = '０１２３４５６７８９'
      const halfWidthNumbers = '0123456789'
      // Create a regular expression to match any full-width number
      const fullWidthRegex = new RegExp(`[${fullWidthNumbers}]`, 'g')
      // Replace full-width numbers with half-width numbers
      return mobileNo.replace(fullWidthRegex, (match) => {
        const index = fullWidthNumbers.indexOf(match)
        return index !== -1 ? halfWidthNumbers[index] : match
      })
    },
    // handle user input
    onInput() {
      if (this.mobileNumber) {
        this.clearErrorMessage()
      }
      this.sanitizeInput()
      const firstThreeDigits = this.getMobileNumberPrefix(3)
      if (this.isInvalidPrefix(firstThreeDigits)) {
        this.errorMessage = '080 または 090 、070 で始まる携帯電話番号が必要です。'
      }
    },
    // clear error
    clearErrorMessage() {
      this.errorMessage = ''
    },
    // get first three prefix of mobile
    getMobileNumberPrefix(length) {
      return this.mobileNumber.slice(0, length)
    },
    // check invalid prefix
    isInvalidPrefix(prefix) {
      return prefix.length === 3 && !['070', '080', '090', '０７０', '０８０', '０９０'].includes(prefix)
    },
    // handle submit of mobile number
    async submitMobileNumber() {
      try {
        if (!this.mobileNumber) {
          this.errorMessage = '電話番号は必須です。'
          return
        }
        if (this.mobileNumber.length > 0 && this.mobileNumber.length !== 11) {
          this.errorMessage = 'この電話番号に誤りがあります。'
          return
        }
        this.isLoading = true
        this.disableBtnClick = true
        const response = await window.axios
          .post('/api/client/send-sms', {
            mobileNumber: this.formattedNumber(),
          })
        this.isLoading = false
        this.disableBtnClick = false
        if (!response.data.error && response.status === 200) {
          this.$emit('smsResponse', {
            mobileNumber: this.mobileNumber,
          })
          // remove tracking back operation after registering mobile number for the first time
          if (this.savedMobile) {
            window.sessionStorage.removeItem('is_back_after_new_mobile_registration')
          }
        } else {
          this.errorMessage = response.data.message
        }
      } catch ({ response }) {
        this.isLoading = false
        this.disableBtnClick = false
        const { data, status } = response
        if (data && status === 422) {
          const { errors } = data
          const [firstErrorMessage] = errors.mobileNumber
          this.errorMessage = firstErrorMessage
        }
      }
    },
    // format mobile number in E.164 format
    formattedNumber() {
      return this.countryCode.concat(
        this.convertToHalfWidth(this.mobileNumber).slice(1, 11),
      )
    },
    isReadOnly() {
      const isBackAfterNewMobileRegistration = window.sessionStorage.getItem('is_back_after_new_mobile_registration') === 'true'
      return this.savedMobile !== '' && !isBackAfterNewMobileRegistration
    },
    onReturnClicked() {
      this.$emit('onMobileReturnClicked')
    },
  },
}
</script>

<style scoped>
.error-text {
  color: red;
  display: block;
}
.b-0 {
  border: none !important;
  margin-bottom: 0 !important;
}
@media screen and (min-width: 896px) {
  .form-group {
    padding: 0;
  }
  .form-control {
    font-size: 16px;
  }
}
</style>
